import { useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { feel } from '@kilat/ui';
import { agentProgress } from '@kilat/shared';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import { academyRepoFor } from '../lib/repositories/academy-repo.js';

/**
 * Training progress on the agent home (gap G18).
 *
 * The agent PWA is a money mirror: earnings, payouts, operators, a chart. For
 * someone who has not signed anyone yet, every one of those renders zero, and
 * the product spends the whole first week telling them they have failed.
 *
 * This card is the counterweight, and WHERE it sits is the feature. It renders
 * ABOVE the earnings chart while the agent is uncertified, so the first thing
 * they see is something that moves when they do the work — reading the field
 * guide — rather than a number that cannot move until an operator says yes.
 *
 * Once certified it disappears from the home screen entirely. A trained agent
 * with signups does not need to be shown a full progress bar every morning;
 * at that point the earnings ARE the story, and leaving this here would be
 * clutter dressed as encouragement.
 */
export function AcademyProgressCard() {
  const t = useI18n();
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  const [completed, setCompleted] = useState<ReadonlySet<string> | null>(null);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    void (async () => {
      try {
        const progress = await academyRepoFor(uid).get('agent');
        if (!cancelled) setCompleted(new Set(progress?.completedLessonIds ?? []));
      } catch (e: unknown) {
        // Silent: a home screen must not show an error for a card that is
        // encouragement. It simply does not render.
        logger.warn('academy progress unavailable on home', { error: String(e) });
        if (!cancelled) setCompleted(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  if (!completed) return null;

  const progress = agentProgress(completed);

  // Certified agents get their home screen back.
  if (progress.certified) return null;

  return (
    <Link to="/academy" style={cardStyle} onClick={() => feel('tap')}>
      <span style={ctaStyle}>{t.t('agentAcademy.homeCta')}</span>

      <span style={trackStyle} aria-hidden>
        <span style={{ ...fillStyle, width: `${Math.round(progress.fraction * 100)}%` }} />
      </span>

      <span style={labelStyle}>
        {t.t('agentAcademy.progress', { done: progress.done, total: progress.total })}
      </span>
    </Link>
  );
}

const cardStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
  padding: 'var(--space-5)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--color-surface)',
  boxShadow: 'var(--shadow-1)',
  textDecoration: 'none',
  color: 'var(--color-on-surface)',
};

const ctaStyle: CSSProperties = { fontWeight: 600 };

const trackStyle: CSSProperties = {
  display: 'block',
  height: 8,
  borderRadius: 999,
  background: 'var(--color-border)',
  overflow: 'hidden',
};

const fillStyle: CSSProperties = {
  display: 'block',
  height: '100%',
  background: 'var(--color-primary)',
  transition: 'width 300ms ease',
};

const labelStyle: CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};
